import { ToneMappingMode } from "postprocessing";
import { PRODUCTION_WEAVE_DENSITY } from "./fabricWeave";
import { DEFAULT_NUDGE_CONFIG, type NudgeConfig } from "./showcaseNudge";

// THE single source of truth for the Stage's look (issue #79, DESIGN.md §5).
//
// Every value the Stage renders with lives in this typed config. The production
// <Viewer> feeds it DEFAULT_STAGE_CONFIG; the dev-only Stage Lab (/stage-lab)
// feeds it a live, control-driven config into the SAME <StageScene>. "Baking" a
// look = pasting the Lab's exported config object back over these defaults. One
// scene component, one config shape — the Lab can never drift from production.
//
// The set is the OBJECT-TURNTABLE SWEEP (ADR-0045, amends ADR-0041 §1): the camera
// and lights are FIXED and the CUSHION rotates. The cushion is presented on a pure
// vertical warm GRADIENT backdrop — a graduation from a lighter warm low to a deeper
// warm high, NEVER a flat colour — with NO floor drawn and NO horizon line (deleted
// by construction: there is no floor edge to reveal at any angle). The weight cue is
// a soft contact shadow cast onto an INVISIBLE catcher (a shadow-only surface) that
// morphs as the cushion turns; the warm-key / cool-accent cinematic separation and a
// frame-edge vignette complete it. Naturalness comes from light + material, not
// furniture. (The earlier sofa set, the golden-hour floating studio, the dark
// pool-of-light, and the ADR-0041 lit-ground-plane + fog cyclorama are all retired.)

export type Vec3 = [number, number, number];

// Post tone-mapping curve. Stored as a string key (stable across the
// `postprocessing` enum's numeric values) and mapped to ToneMappingMode at the
// effect. ACES is the production default; AgX/Neutral/… are exposed in the Lab
// because the curve strongly drives the Stage's warmth + saturation.
export type ToneMappingKey = "ACES_FILMIC" | "AGX" | "NEUTRAL" | "REINHARD" | "CINEON" | "LINEAR";

export const TONE_MAPPING_MODES: Record<ToneMappingKey, ToneMappingMode> = {
  ACES_FILMIC: ToneMappingMode.ACES_FILMIC,
  AGX: ToneMappingMode.AGX,
  NEUTRAL: ToneMappingMode.NEUTRAL,
  REINHARD: ToneMappingMode.REINHARD,
  CINEON: ToneMappingMode.CINEON,
  LINEAR: ToneMappingMode.LINEAR,
};

export interface DirectionalLightConfig {
  position: Vec3;
  intensity: number;
  color: string;
}

// The warm KEY (ADR-0041 §1, kept by ADR-0045 §4): a SPOTLIGHT high and slightly
// front, aimed at the cushion. It is the cushion's dominant light AND the sole
// shadow-caster — under the FIXED camera a rotating cushion sweeps this key across
// its folds so the self-shadow AND the soft contact shadow (on the invisible catcher)
// both animate in real time (the whole realism lever, ADR-0045). `penumbra` softens
// the cone edge so the direct shading reads as a designed key, never a hard circle.
// Kept NEAR-NEUTRAL so the pet reads true (the True-Pet Rule); the warmth is in the
// surround (gradient field/fill), not in a cast on the cushion.
export interface KeyLightConfig {
  position: Vec3;
  intensity: number;
  color: string;
  // Cone half-angle (radians) — the spread of the key's direct shading on the cushion.
  angle: number;
  // Soft pool edge (0..1); high keeps it a gradient, never a hard circle.
  penumbra: number;
}

// The grazing KICKER (ADR-0044 light-first): a low, side-on directional light that
// RAKES across the cushion's front face so the Gathers (geometry creases radiating
// from the seam) self-shadow and read by DIRECT shading — present whether or not
// SSAO runs. It is core lighting, NEVER on the degradation ladder, so the crease
// read survives down to the weakest mid-range phone (where SSAO is the first thing
// shed). Kept NEAR-NEUTRAL: it is a SHAPING light, not a colour cast — the pet must
// still read true (the True-Pet Rule, like the key). Casts no shadow (one shadow-
// caster — the key — holds the mobile 60fps budget; the rake is N·L on the Gather
// walls, which needs no shadow map). A directional (not a spot) so it adds an even
// grazing rake instead of a bright floor pool that could re-introduce the #162 glare.
export interface KickerLightConfig {
  position: Vec3;
  intensity: number;
  color: string;
}

// The signature cool ACCENT (ADR-0041 §1, rev. 2026-06-29): a restrained cool
// teal/blue directional from BEHIND-and-to-the-side of the cushion. Coming from
// behind, it catches only the cushion's upper/edge contour (the rim) and spills
// onto the field periphery — the warm-key / cool-accent cinematic separation that
// lifts the cushion off the warm field. Because it lights the back rim, never the
// camera-facing face, it CANNOT tint the pet's face (the True-Pet Rule holds by
// construction). Casts no shadow. Hue/strength are live Stage-Lab knobs.
export interface AccentLightConfig {
  position: Vec3;
  intensity: number;
  color: string;
}

export interface LightingConfig {
  // drei <Environment> IBL fill level (0–1 in practice; kept low so it fills
  // and reflects without washing out the directional contrast / normal map).
  environmentIntensity: number;
  // A tiny warm ambient floor so the contact shadow reads warm-dark, never pure
  // black (ADR-0041 §1: a warm field, not a black void).
  ambient: number;
  // The warm key spotlight high-and-front — the cushion's dominant light AND the
  // sole shadow-caster (both the self-shadow and the invisible-catcher contact shadow).
  key: KeyLightConfig;
  // Low warm fill from the opposite low side: lifts the shadow side so it doesn't
  // crush to black. Casts no shadow.
  fill: DirectionalLightConfig;
  // The grazing kicker that rakes the seam so the Gathers read without SSAO
  // (ADR-0044 light-first). Core lighting, never shed.
  kicker: KickerLightConfig;
  // The cool teal/blue rim accent that pops the cushion off the warm field.
  accent: AccentLightConfig;
}

// The invisible shadow CATCHER (ADR-0045 §5): with the camera fixed, the ground
// plane + horizon machinery of ADR-0041 §1 is gone — no floor is drawn at all.
// Instead a shadow-ONLY surface (a `ShadowMaterial` plane) sits beneath the cushion
// and RECEIVES the warm key's real-time contact shadow: the shadow reads (weight),
// but the surface itself is invisible, so there is no floor and no horizon line to
// reveal at any angle. As the cushion rotates under the fixed key the cast shadow
// MORPHS shape in real time. The pure gradient backdrop shows THROUGH the catcher
// everywhere except where the shadow darkens it.
export interface CatcherConfig {
  // World-Y of the invisible catcher plane — the cushion's lowest point rests on it
  // (cushionRestPose) and its contact shadow lands on it.
  y: number;
  // The contact-shadow tint. A warm-dark (not pure black) so the shadow reads warm
  // against the warm gradient — the ambient floor keeps it from crushing to black.
  shadowColor: string;
  // Contact-shadow strength on the invisible catcher (0..1): the ShadowMaterial's
  // opacity. Higher = a darker, more grounding shadow (Leon 2026-07-01: "the shadow
  // should be stronger"); 0 = no shadow (the cushion reads weightless/sticker-like,
  // the rejected alternative in ADR-0045 §5).
  shadowOpacity: number;
}

// The pure gradient BACKDROP (ADR-0045 §5): a large enclosing dome with an unlit
// vertical gradient — a graduation from a lighter warm LOW (`poolColor`) to a
// deeper warm HIGH (`fieldColor`), NEVER a flat colour (which reads cheap). It
// depends only on the view ray's elevation, so it is azimuthally symmetric — there
// is no bright-one-side / dark-other-side split and, being a dome with no floor
// beneath it, NO horizon line to reveal at any angle. It is the "designed glowing
// studio field," not a room and not a black void.
export interface BackdropConfig {
  // The lighter warm at the LOW end of the gradient (below + around the cushion).
  poolColor: string;
  // The deeper warm the gradient graduates to toward the HIGH end (overhead).
  fieldColor: string;
  // Elevation (0 = straight down, 0.5 = horizon, 1 = straight up — the view ray's
  // y remapped over the full sphere) where the low→high fade BEGINS.
  poolY: number;
  // Vertical SPAN of the fade (in the same remapped-elevation units) — how much of
  // the dome the graduation covers. Kept wide so the gradient reads across the frame.
  poolSize: number;
  // Fade curve softness (>1 = a wider, gentler graduation; holds more of the low
  // warm in frame before it deepens).
  falloff: number;
}

// The soft contact shadow (ADR-0045 §5): the warm key casts a real-time shadow map;
// the cushion casts, the invisible catcher receives — pooled soft-edged and tight to
// the silhouette directly beneath the cushion (the weight cue). `mapSize` is the BASE
// (max) resolution — the degradation ladder steps it down (shadowMapSizeForCutLevel)
// to hold the 60fps floor on mobile, never to zero (the shadow is the weight cue).
export interface ShadowConfig {
  mapSize: number;
  bias: number;
  normalBias: number;
  // PCF blur radius (texels) + sample count: softens/anti-aliases the shadow's
  // edge so the contact shadow is soft, not a hard pixelated outline. Higher =
  // softer, costlier.
  radius: number;
  blurSamples: number;
}

// The render-time cloth surface pass ("Brushed cotton", signed off 2026-06-07 —
// GOAL B). Applied on the baked MeshStandardMaterial via onBeforeCompile (see
// components/pillowFabric.ts), so it never re-bakes (ADR-0033 §1) and never
// washes the pet (the True-Pet-safe successor to the rejected #83 sheen). Gated
// by the `pillowFabric` degradation rung — cut, it reverts to the baked-normal look.
export interface PillowFabricConfig {
  roughness: number; // base matte level (cloth is rough; multiplies the baked roughness map)
  weaveDensity: number; // procedural thread crossings per UV unit (high = fine cloth)
  weaveDepth: number; // procedural weave normal amplitude
  // Render-distance persistence of the weave (#168). The fabric pass band-limits the
  // weave with an fwidth roll-off so it can't crawl/moiré when threads go sub-pixel.
  // weaveFade scales WHERE that roll-off sits: 1.0 = the original Nyquist band (the
  // weave only read on extreme close-up); >1 slides the roll-off to a finer per-pixel
  // thread so the weave SURVIVES to mid distance, while its upper edge still kills it
  // before it can moiré. Fully resolves on close-up / Inspect regardless. Live knob.
  weaveFade: number;
  fuzz: number; // rim fuzz strength (faint; grazing-only, never face-on)
  fuzzPower: number; // rim tightness (higher = thinner rim)
  fuzzColor: string; // fuzz tint (warm-white reads as a fiber halo)
  // Per-vertex cavity-AO strength (#200). The worker bakes a scalar occlusion attribute
  // (glTF `_ao`) from the settled cloth's concavity — dark in fold valleys, ~1 on open
  // faces. This knob scales how deep the valleys darken: it multiplies the DIFFUSE LIGHTING
  // (not the pet albedo) so creases read deep/soft without crushing the print or the matte.
  // 0 = off (also the fallback when the mesh carries no `_ao`, e.g. the static rectangle).
  // Always-on, off the degradation ladder; SSAO stays a bonus layer on top. Live knob.
  cavityAo: number;
  // Matte-cotton specular attenuation (2026-07-10). The ONE authoritative "how reflective
  // is this cloth at all" knob. It scales the base DIELECTRIC specular — the ~4% F0 lobe
  // every direct light (key/kicker/accent/fill) AND the scene IBL leave on any non-metal.
  // This is the lever the operator's "looks like a thin glass coat over the print" needed:
  // `roughness` only WIDENS that lobe (even at 1.0 it survives) and `metalness` is 0, so
  // no roughness/env value removes it, and at grazing angles Fresnel ramps it toward 1.0
  // (the glassy rim under the raking key). This scales BOTH the direct and indirect (env)
  // specular — the render-time stand-in for the specular-scattering microfibre of real
  // matte cotton. Specular-channel only ⇒ True-Pet-safe (cannot recolour the pet albedo).
  // 1 = full PBR spec; ~0.2 = matte cotton; 0 = pure chalk (operator wants matte, not zero).
  specularScale: number;
  // Warm specular integration (#203). The "sits in the warm light" win #198 could not
  // deliver on the flat mesh (it is gated on cushion VOLUME — now present via the #199
  // cloth sim). Tints the cushion's DIRECT specular lobe warm so it reads as a real
  // object IN the warm light: the near-neutral key/kicker (kept neutral for the True-Pet
  // Rule) leave a near-white specular on the lit ridges once `roughness` drops off the
  // matte ceiling, and this warms THAT specular. Because it rides on the SPECULAR channel
  // only it sits on top of the pet albedo and CANNOT recolour it (the same reasoning that
  // makes envMapIntensity True-Pet-safe), and being direct-light-driven it warms only lit
  // ridges — a form gradient across the volume, never a flat wash, so it can NOT flatten
  // form (the #67/#198 reason warming the global neutral IBL was rejected; this is the
  // sanctioned alternative). 0 = off. Live knob; final value baked in #201.
  warmSpec: number;
  // The warm hue the specular lobe is tinted toward (a soft amber sunlight). Multiplies
  // the specular, so it colours only the highlight, never the pet's midtones.
  warmSpecColor: string;
}

export interface PillowConfig {
  // Lean/tilt about X (radians). POSITIVE tips the cushion BACK so the pet's face
  // presents toward the (slightly elevated) camera — top tipping toward −z (the
  // scene negates it into three's +X-is-forward convention). Composes OUTSIDE the
  // 180° flip: the lean holds while the flip turns the pillow to its back in place.
  // Fed to cushionRestPose (ADR-0041 §3) — the designed face-presentation knob.
  leanAngle: number;
  // The cushion's resting DEPTH on the sweep (world-Z of its centre). X is pinned
  // to the sweep centre (0); Y is derived so the lowest leaned point rests on the
  // ground (cushionRestPose). A Stage-Lab knob — slide it forward/back in the field.
  restZ: number;
  // Woven-fabric depth (#83, DESIGN.md §5 surface pass): a render-time multiplier
  // on the baked normal map's strength, so the grazing key light rakes the weave
  // + folds into real cloth instead of a flat slab. Unlike a sheen layer this
  // NEVER touches the pet's colour/saturation — it only changes how light catches
  // the baked surface bumps. 1.0 = the baked strength. Render-time only (ADR-0033
  // §1 — no re-bake); the folds stay in the map, never the mesh.
  normalScale: number;
  // Pet exposure compensation (#122): an untinted grey scale (0..1) on the pet
  // material's albedo response — diffuse-equivalent to dimming the light on the
  // pet ONLY. The warm key (aimed at the cushion) can overexpose the pet into the
  // ACES shoulder, which rolls its colour off to desaturated white (the "white
  // veil"); this knob restores the pet's True-Pet colour without touching the
  // field/ground/pool. 1.0 = no compensation.
  exposure: number;
  // Env reflection strength (#198): the pillow material's `envMapIntensity` — how
  // strongly the matte cloth picks up the scene IBL as a soft specular signature,
  // so it reads as a real object sitting IN the light rather than a flat decal lit
  // from nowhere. Distinct from `lighting.environmentIntensity` (the scene-wide IBL
  // level): this is the per-material multiplier on top of it. Render-time only — a
  // specular term, so (unlike a diffuse wash) it can't recolour the pet's albedo;
  // kept modest so it never floods the normal/fold contrast flat (the #67 lesson).
  envMapIntensity: number;
  // The render-time cloth surface pass (weave + rim fuzz). See PillowFabricConfig.
  fabric: PillowFabricConfig;
}

export interface CameraConfig {
  fov: number;
  // Zoom (dolly) clamp along the FIXED view axis (ADR-0045 §2): the camera never
  // orbits, so these bound how close/far the user can dolly toward the cushion.
  minDistance: number;
  maxDistance: number;
}

export interface BloomConfig {
  intensity: number;
  luminanceThreshold: number;
  luminanceSmoothing: number;
}

export interface SsaoConfig {
  intensity: number;
  radius: number;
  luminanceInfluence: number;
  samples: number;
}

// The frame-edge VIGNETTE (ADR-0041 §1): a screen-space post effect that darkens
// toward the frame edges, so the warm field graduates from the bright pool behind
// the cushion to darker edges — the gentle vignette the look spec calls for. A
// pure pixel function (deterministic, no per-frame state → demand-render idle stays
// at 0 frames), cheap, and on no device tier is it load-bearing for the crease read.
export interface VignetteConfig {
  // Where the darkening begins (0 = centre, 1 = corner). Lower = a larger dark band.
  offset: number;
  // Strength of the darkening at the edges (0 = none, 1 = black corners).
  darkness: number;
}

export interface PostConfig {
  toneMapping: ToneMappingKey;
  bloom: BloomConfig;
  ssao: SsaoConfig;
  vignette: VignetteConfig;
}

// Note (#162): the film GRAIN (a per-frame <Noise> pass, #82) is retired. Its only
// real job was dithering the dark fog/field gradient against 8-bit banding; that's
// now a static, deterministic output dither baked into the post stack (no knob —
// its amplitude is a fixed correctness value, not a look-tune; see
// components/ditherEffect.ts).

// The 60fps degradation ladder (#82, DESIGN.md §5, ADR-0041 §5). `tier` picks how
// much of the look is on; `autoDowngrade` lets a runtime FPS monitor step the
// tier down so a mid-range phone holds the 60fps floor. The cut ORDER + the
// shadow-map-resolution steps live in degradation.ts; the core look (warm field,
// warm pooled key, the grazing kicker, the soft contact shadow) never flattens.
// "ultra" is the full look and the production default.
export type QualityTier = "ultra" | "high" | "medium" | "low";

export interface QualityConfig {
  tier: QualityTier;
  autoDowngrade: boolean;
}

// The generation reveal (ADR-0041 §2, "materialize at rest", #108): a CLIENT-SIDE
// one-shot played over the worker's deterministic artifacts. Over one short beat
// the finished cushion fades in (opacity 0→1) already at its fitted rest pose on
// the sweep — it never moves, never inflates, never drops.
export interface RevealConfig {
  // Whole-reveal play time in seconds (drives the Lab "Play" + prod auto-play).
  durationSec: number;
  // The one-shot showcase NUDGE (ADR-0045 §7, #215): a gentle rotate-and-settle on
  // the CUSHION played once right after the reveal settles — the "you can turn me"
  // hint for the object-turntable interaction. NOT a continuous spin (idle stays at
  // 0 frames); a no-op under prefers-reduced-motion. Angle + duration are a live
  // sign-off tune, verified on the real-GPU harness.
  nudge: NudgeConfig;
}

export interface StageConfig {
  // The warm clear colour behind everything (Canvas clear colour). The backdrop
  // dome covers it in view; matched to the field so any sliver is seamless.
  backgroundColor: string;
  lighting: LightingConfig;
  // The invisible shadow catcher (ADR-0045 §5) replaces the drawn ground plane + fog.
  catcher: CatcherConfig;
  backdrop: BackdropConfig;
  shadow: ShadowConfig;
  pillow: PillowConfig;
  camera: CameraConfig;
  post: PostConfig;
  // The 60fps degradation ladder (#82): which tier of the look is on + whether a
  // runtime monitor may step it down.
  quality: QualityConfig;
  reveal: RevealConfig;
}

// Front-door warmth (#203 follow-up, operator sign-off 2026-07-01, variant "B").
// The EMPTY stage (no pet yet) is the "show before you ask" front door (PRODUCT.md)
// — it should glow warm and inviting. The WITH-pillow rig is tuned around the pet as
// the focal contrast (#188): a near-neutral field, a cool accent popping the cushion
// rim, ACES capping the warm. With NO pet, that same rig leaves the empty field muted
// and cool-grey at the top (the accent spilling with no rim to catch), which read as
// "flat" (operator report). So when the stage is EMPTY these deltas warm the sweep:
// the cool accent is pulled almost out (nothing to rim-light), the warm pool is
// brighter/larger/lifted, and the ambient floor rises. Applied ONLY when empty
// (StageScene) — the signed-off with-pillow look and the True-Pet Rule are untouched
// (there is no pet on the empty stage). The reveal's "lights-down" start masks the
// swap back to the neutral rig as a cushion lands, so there is no visible snap.
export const EMPTY_STAGE_WARMTH = {
  accentIntensity: 2,
  ambient: 0.2,
  // #216: brightened #ffc774 → #ffdca8 to stay above the with-pet pool after the amber
  // re-hue (the empty front door must read brighter/warmer than the pet rig) and to
  // harmonise with the new golden gradient.
  poolColor: "#ffdca8",
  poolSize: 0.62,
  poolY: 0.42,
} as const;

// The camera's position + orientation are FIXED for the whole session (ADR-0045 §1:
// the camera never orbits — the CUSHION turns). Dead-FRONT hero view: on the +z axis
// (x = 0) so the camera looks straight at the cushion's front, slightly elevated (y)
// so it looks gently DOWN at the leaned cushion — the face presents toward it
// (ADR-0041 §3). Bounds overrides the DISTANCE (it frames the cushion) and the user's
// zoom dollies ALONG this view direction, so only the direction is fixed here.
export const CAMERA_POSITION: Vec3 = [0, 2.5, 5];

// DEFAULTS = the recommended production look. The Lab opens here, so the first
// frame is exactly what ships; every export is a delta from this baseline. The
// hues, accent strength, vignette, contact-shadow tightness, and lean were dialled
// in the #188 look sign-off on the REAL pet (Cat_August) via the real-GPU harness
// (the /shot software-GL harness drops the real-time shadows + lighting this look
// hinges on, so it cannot judge it) and confirmed live on the operator's display:
// the "Balanced" warm direction (richer gold pool, deeper vignette, a stronger cool
// rim) + grounding option A (a tightened contact shadow under a gentle hero float).
export const DEFAULT_STAGE_CONFIG: StageConfig = {
  // A warm amber clear colour the backdrop dome sits over (matched to the field so
  // no edge shows). Warm, never a black void (ADR-0041 §1). #216: warmed #3a2109 →
  // #883d20 with the new amber gradient (the old deep brown "read like mud", Leon).
  backgroundColor: "#883d20",
  lighting: {
    // Scene IBL fill. (2026-07-10 operator live-set to 0.2 for the target preview —
    // a fuller soft fill on the shadow side. The env SPECULAR this would otherwise add
    // as a sheen is held in check by fabric.specularScale, which scales the indirect/env
    // specular down too, so a brighter IBL lifts the DIFFUSE fill without the glass look.)
    environmentIntensity: 0.2,
    // Trimmed 0.14 → 0.10 (2026-07-01), lifted 0.10 → 0.13 (#212): with the cushion
    // now free to rotate to ANY angle (ADR-0045), a face turned fully away from both
    // the key and the (directional) fill has ONLY this omnidirectional floor + the IBL
    // to keep it off pure black — the ADR-0045 §4 "no cushion face is ever pure black
    // at any rotation" guarantee. Kept as low as that guarantee allows so the key's
    // contact shadow + directional form still read (the #216 live balance vs the
    // 2026-07-01 "shadow too weak" note). Warm floor so the shadow reads warm-dark.
    // (2026-07-10 matte pass: 0.13 → 0.11 → 0.09 — less overall lift so the lit face
    // sits out of the ACES white shoulder and the shadow side deepens; still above the
    // no-pure-black floor with the fill + env behind it at any rotation.)
    ambient: 0.09,
    // The warm key SPOTLIGHT, the SOLE shadow-caster (ADR-0045 §3: one shadow-casting
    // light produces BOTH the contact shadow AND the cushion's real-time self-shadow).
    // Moved lower + more to the side (#212): [2.4, 9, 3.4] (elev ~65°) → [4.6, 6.2, 3.0]
    // (elev ~48°, further front-right). The lower, more grazing rake is the whole
    // realism lever — under the fixed camera a rotating cushion sweeps this raking key
    // across its folds so the fold self-shadows throw long and legible and MOVE in real
    // time. Kept front-right enough (z 3.0) that at the home/rest pose the camera-facing
    // pet is well lit and TRUE (priority = faithful pet at rest) with only a note of
    // rake; the drama grows as the cushion turns the folds into the grazing light.
    // Gently warm but NEAR-NEUTRAL (saturation < 0.15) — the non-negotiable True-Pet
    // Rule. Grazing angle/elevation are a #216 real-GPU live tune (headless /shot drops
    // the real-time shadow map this hinges on and cannot judge it).
    // Raised 6.2 → 6.7 (ADR-0046 pan-clearance hover): the cushion now rests
    // hovering its own spin radius (~0.45 for the committed sample) above the
    // catcher so a full downward pan keeps its contact shadow; lifting the key by
    // the same order keeps its elevation from the (now higher) cushion at the #212
    // ~50° rake, so the fold shading and the shadow's throw read as signed off. A
    // live-tune candidate with the hover look itself.
    key: {
      // 2026-07-10 matte pass: moved further to the SIDE and lower — [4.6,6.7,3.0] →
      // [6.2,4.8,1.6] (a more grazing side-rake so the far rounded flank falls into a
      // legible self-shadow). Intensity operator live-set to 7 for the target preview
      // (rebalanced with CINEON tone mapping + exposure 0.9 below — the brighter key
      // reads without blowing out because specularScale kills the glassy highlight).
      position: [6.2, 4.8, 1.6],
      intensity: 7,
      // Gently warm, near-neutral — the non-negotiable True-Pet Rule (DESIGN.md
      // §5). The warm field comes from the backdrop/fog/fill, NOT a warm cast.
      color: "#fff2e4",
      angle: 0.85,
      // Tightened 0.6 → 0.48 (#198) → 0.38 (#203): a crisper lit→shadow falloff so the
      // warm key reads as a directional KEY shaping the cushion's form — a readable
      // lit→shadow form GRADIENT across the now-volumetric cloth-sim cushion (#199), not
      // a flat even wash. The cone stays wide (angle 0.85) so the ground pool is still a
      // soft gradient, never the #162 hard spotlight circle; the contact shadow softness
      // is the separate shadow.radius/blurSamples. A #203 starting point (final tune #201).
      penumbra: 0.38,
    },
    // Low warm fill from the opposite low side; casts no shadow. Gentle so it lifts
    // the shadow side without flattening the contact shadow. Nudged 0.22 → 0.26 (#212)
    // to soften the key-away faces now that the cushion rotates freely (directional, so
    // it complements — not replaces — the omnidirectional `ambient` floor that carries
    // the no-pure-black guarantee). A #216 live-tune candidate alongside the key rake.
    // (2026-07-10 matte pass: 0.26 → 0.17 — a deeper shadow side is wanted now; the
    // lower fill lets the far flank sit in real shadow. Still lifts it off pure black.)
    fill: { position: [-4, 1.8, -1.5], intensity: 0.17, color: "#ffd9b0" },
    // The grazing KICKER (ADR-0044 light-first): cross-key to the warm key (front-
    // RIGHT-high), this rakes in from front-LEFT and LOW so it skims the cushion's
    // front face — the Gather walls light up while the flat field stays dark,
    // carving the grooves by direct shading with NO dependence on SSAO. NEAR-NEUTRAL
    // warm-white (saturation < 0.1) so it shapes without tinting the pet. Live knobs.
    // (2026-07-10 matte pass: 2.2 → 1.0 — the kicker rakes from the front-LEFT-low, i.e.
    // straight into the flank we now want shadowed, and being grazing it was a big base-
    // specular "glass" source; trimmed so it still hints the front grooves without filling
    // the shadow or glinting. The global specularScale below is what truly kills its glint.)
    kicker: { position: [-4, 1.4, 1.8], intensity: 1.0, color: "#fff3e8" },
    // The signature cool ACCENT: a teal/blue rim from behind-left-high. It catches
    // the cushion's upper/back contour + spills cool light on the field periphery
    // (the warm-key / cool-accent pop), and because it comes from behind it never
    // touches the camera-facing face (True-Pet Rule). Restrained — it lifts the
    // edge, it does not light the cushion. Intensity 8 / cooler teal #3aa6dc are the
    // #188 signed-off values (the previous 5 / #4aa8e6 barely read on the field).
    // Trimmed 8 → 6 (2026-07-01): the strong cool teal spill neutralised the warm
    // key on the FLOOR into a colourless gray wash (Leon: "where the light shines
    // it's just white, you can't see the colours"). At 6 the cool rim still pops the
    // cushion's back contour, but the floor keeps its warm tone. A live-tune candidate.
    accent: { position: [-2.5, 3.5, -5], intensity: 6, color: "#3aa6dc" },
  },
  // The invisible shadow catcher (ADR-0045 §5): NO floor is drawn — a shadow-only
  // ShadowMaterial plane at `y` receives the warm key's real-time contact shadow (the
  // weight cue) and the cushion rests its lowest leaned point on it (cushionRestPose).
  // The pure gradient backdrop shows through everywhere except under the cushion. The
  // shadow reads warm-dark (not black) against the warm gradient, and is kept fairly
  // strong (Leon 2026-07-01: "the shadow should be stronger") so the floating cushion
  // still reads grounded. shadowColor/shadowOpacity are #216 real-GPU live tunes.
  catcher: {
    // 2026-07-10 operator live-set -0.5 → -2 for the target preview: the invisible
    // shadow plane sits lower, so the cushion reads as floating higher above a softer,
    // more distant contact shadow (less "planted", more hero-float).
    y: -2,
    shadowColor: "#1a1005",
    shadowOpacity: 0.6,
  },
  // The pure gradient backdrop (ADR-0045 §5): an azimuthally-SYMMETRIC vertical
  // gradient over the FULL dome — a lighter warm low (poolColor) graduating to a
  // deeper warm high (fieldColor), NEVER a flat colour, with no floor beneath it and
  // so no horizon line at any angle. Because it depends only on the view ray's
  // elevation it is identical at every heading — no bright-one-side / dark-other-side
  // split. Muted warm, NOT saturated orange (Leon 2026-07-01). A wide fade over the
  // whole sphere so the graduation reads across the framed view (never a flat band).
  // Hues + fade are #216 real-GPU live tunes.
  // #216 sign-off (real GPU, real pet): the old sand→brown gradient "read like mud"
  // (Leon). Re-hued to a warm AMBER hero — a golden low graduating to a warm terracotta
  // high — brand-adjacent (the site's coral/orange) and inviting, with the pet still
  // popping. Dialled live as the "between vibrant-sunset and peach-cream" midpoint.
  backdrop: {
    // The lighter warm at the LOW end (below + around the cushion): a soft GOLDEN amber.
    poolColor: "#ffcf90",
    // The deeper warm the gradient graduates to at the HIGH end (overhead): a warm
    // terracotta/amber — richer than the old brown, never near-black (a black "sky"
    // reads as broken); it gives the cushion a warm deeper field to sit against.
    fieldColor: "#c4693a",
    // The fade spans essentially the whole sphere (begins at the bottom, spans up to
    // the top) so every part of the framed backdrop is a graduation, never a flat
    // band — the ADR-0045 §5 "graduation, never a flat colour" guarantee. A touch >1
    // holds a little more of the golden low before it deepens (the premium graduation).
    poolY: 0.0,
    // 2026-07-10 operator live-set 1.05 → 0.7: a tighter golden-low band that deepens
    // to the terracotta high sooner (a more contained pool behind the cushion).
    poolSize: 0.7,
    falloff: 1.1,
  },
  // The soft contact shadow's real-time map. 2048 base, stepped down by the
  // degradation ladder; a soft (radius/blurSamples) PCF edge so it's not a hard outline.
  shadow: {
    mapSize: 2048,
    bias: -0.0006,
    normalBias: 0.02,
    // Tighter PCF (radius 3) than the #188 4/16 — the 2026-07-01 pass: the contact
    // shadow read too faint/soft (Leon: "the pillow's shadow is too weak, it should
    // be stronger"), so a crisper edge + the trimmed ambient/fill below deepen it
    // into a real anchoring cast shadow (ADR-0041 §1 "weight from the shadow").
    radius: 3,
    blurSamples: 16,
  },
  // The pet cushion lays on the flat sweep ground at a tuned lean that presents the
  // pet's face to the slightly-elevated camera (ADR-0041 §3, cushionRestPose). The
  // lowest leaned point rests on the ground (ground.y); restZ is the resting depth.
  // All Stage-Lab tunes, signed off live on the real pet (#188).
  pillow: {
    // ~9°: a gentle backward lean so the face tilts up toward the camera. The #188
    // face-presentation decision: a SOFTER lean than the prior 0.3 — the bigger lean
    // lifted the cushion higher (it floated), so this trades a little face-tip for a
    // gentler hero float that the tightened contact shadow grounds. (The cushion is
    // presented as a deliberate floating hero, grounding option A — #188.)
    leanAngle: 0.15,
    // Centred on the sweep depth. Slide forward/back in the field as a framing tune.
    restZ: 0,
    // A touch above the baked 1.0 so the weave reads as fabric under the grazing
    // key without looking embossed; the final value is a Stage Lab tune. STILL A NO-OP
    // (#203): #200 baked crease depth as per-VERTEX cavity AO (the `_ao` scalar), NOT a
    // normal MAP — fold normals remain carried as GEOMETRY (no normalMap, no UV-sampled
    // ORM, so the #150 comb cannot return). normalScale only scales a normal MAP, of
    // which the mesh has none, so it does nothing today; the fold form the warm key
    // catches comes from the real cloth-sim GEOMETRY, and the weave from the render-time
    // procedural pass below. Kept wired for when a relief bake reintroduces a normal map.
    normalScale: 1.4,
    // Pet exposure compensation (#122): keeps the pet's effective light in the
    // near-linear part of ACES (out of the white-veil shoulder) under the warm key.
    // pet ≈ key × exposure; tuned live on the real pet (#188). (2026-07-10 matte pass:
    // 0.85 → 0.78 → 0.90 (operator live-set for the target preview): rebalanced with the
    // brighter key (7) + CINEON tone mapping; CINEON's gentler shoulder lets the pet sit
    // brighter without bleaching, and specularScale keeps the extra light from glinting.)
    exposure: 0.9,
    // Env reflection strength (#198): the pillow's `envMapIntensity`. KEPT at 1.0
    // (three's material default, = the prior implicit ship value). The #198 harness
    // A/B found this lever is effectively a NO-OP on the current Stage: the IBL is a
    // dim NEUTRAL studio HDRI at scene intensity 0.08, so the cloth's env term is
    // both faint and not warm — cranking this 1.0 → 2.4 was visually identical, and
    // dropping it would only reduce the (already modest) reflection. Wired as a live
    // knob anyway (Lab + cfg) for when a warmer/brighter env or a relief bake makes
    // it meaningful; warming the global IBL to force it was rejected (faint cast on
    // the pet → True-Pet risk, + it flattens form per #67). The "sits in the warm
    // light" win is gated on cushion VOLUME (the #195 cloth-sim pillar), not this.
    // (2026-07-10 matte pass: 1.0 → 0.05 — the env reflection was part of the "glass
    // coat" the operator flagged; dropped near-off. fabric.specularScale below now
    // also scales the indirect/env specular, so this and that pull the same direction.)
    envMapIntensity: 0.05,
    // Matte print cotton — 2026-07-10 look pass (supersedes the #203 "warm specular
    // signature" direction). Operator report: the cushion read like "a thin glass coat
    // over the print — a strong reflection of the incident light," not cloth. Root cause
    // (verified against the GLB + shader): the base DIELECTRIC specular (the ~4% F0 lobe
    // every direct light + the IBL leave on any non-metal) — which `roughness` only
    // WIDENS (it does NOT remove it) and `metalness`=0 already, so NO roughness/env value
    // could kill it — spiked by Fresnel under the raking key. So the direction flipped:
    //   • roughness → 1.0 (max): the GGX lobe is as broad/soft as MeshStandardMaterial
    //     allows — a dim wide sheen, never a tight glare;
    //   • warmSpec → 0: it MULTIPLIED the direct specular back in (warm-tinted), i.e. it
    //     was AMPLIFYING the very "glass" the operator disliked — off;
    //   • specularScale (new, pillowFabric.ts) → the real lever: scales the residual base
    //     dielectric specular (direct + env) down to a matte-cotton whisper.
    // The cushion's "life" now comes from the fold form (side key + self-shadow) and the
    // cavity-AO valleys, not from a specular sheen — which matches the matte-linen refs
    // (REFERENCES.md), not satin. weaveDepth stays ~0 (the #198 moiré kill); weaveDensity
    // + weaveFade unchanged so a properly-sampled weave returns in one edit when factory
    // samples arrive. Live-tune candidates on the real-GPU harness; final operator sign-off.
    fabric: {
      roughness: 1.0,
      weaveDensity: PRODUCTION_WEAVE_DENSITY,
      weaveDepth: 0,
      weaveFade: 2.0,
      fuzz: 0.2,
      fuzzPower: 2.0,
      fuzzColor: "#fff1e0",
      // Per-vertex fold-valley darkening. The bake's deepest creases sit ~0.6 (0.4 range);
      // this dims their diffuse light so the folds read deep + soft while open faces (AO ~1)
      // are untouched. (2026-07-10 matte pass: 0.6 → 0.82 — with the specular sheen gone the
      // form has to come from shading, so the valleys carry more of the crease read.) LIVE
      // tune vs .scratch/realism-refs on the real-GPU harness — must not crush the pet/matte.
      cavityAo: 0.82,
      // Matte-cotton specular attenuation (2026-07-10) — the real fix for the "glass coat"
      // (see PillowFabricConfig.specularScale). 1 = full PBR spec; here dialed down to a
      // matte-cotton whisper so the base dielectric lobe stops reading as a glassy reflection
      // of the key, while a trace of sheen keeps the lit ridges from going dead-chalk. Live
      // tune on the real-GPU harness; final operator sign-off.
      specularScale: 0.25,
      // Warm specular integration (#203) — OFF as of the 2026-07-10 matte pass. It MULTIPLIED
      // the direct specular back in (warm-tinted), i.e. it amplified the exact "glass" the
      // operator disliked; with the matte-cotton direction there is no specular signature to
      // warm. Kept wired (uniform + knob) so a future satin/sheen variant is one edit away.
      warmSpec: 0.0,
      warmSpecColor: "#ffb37a",
    },
  },
  camera: {
    fov: 35,
    // The zoom (dolly) clamp along the fixed view axis (ADR-0045 §2). The
    // establishing fit frames the CUSHION close; minDistance lets the user dolly in
    // on the print/weave, maxDistance caps the pull-back to a sensible wide.
    minDistance: 0.2,
    maxDistance: 6,
  },
  post: {
    // 2026-07-10 operator live-set ACES_FILMIC → CINEON for the target preview: CINEON's
    // filmic curve gives a warmer, gentler highlight roll-off than ACES (which capped the
    // warm gold hard — see the #183/#216 notes), so the amber field + lit pet read richer.
    toneMapping: "CINEON",
    // Restrained bloom so the warm pool + the key sheen lift into a soft glow,
    // never neon (the look is toned, not Tron). A slightly lower threshold lets the
    // bright amber pool bloom gently — the "designed glow".
    // (2026-07-10 matte pass: intensity 0.24 → 0.10 — bloom blooms the brightest pixels,
    // i.e. the lit fabric ridges, into a glow that reads as SHINE; trimmed so the warm
    // pool still lifts gently but the cushion surface no longer haloes into a sheen.)
    bloom: { intensity: 0.1, luminanceThreshold: 0.8, luminanceSmoothing: 0.3 },
    ssao: { intensity: 1.2, radius: 0.12, luminanceInfluence: 0.5, samples: 16 },
    // The frame-edge vignette: graduates the warm field darker toward the edges so
    // the bright pool behind the cushion reads as a designed glow, not a flat wash.
    // #216: lightened .72 → .5 (offset .26 → .3) alongside the amber re-hue — the deep
    // #188 vignette crushed the new gradient's edges back toward the "muddy" brown; the
    // lighter band keeps the pool glowing while still popping the cushion off the field.
    // 2026-07-10 operator live-set for the target preview: offset .3 → .40 (darkening
    // starts further out, a larger clear centre) + darkness .5 → .61 (deeper edges).
    vignette: { offset: 0.4, darkness: 0.61 },
  },
  // Production ships the full look ("ultra") with runtime auto-downgrade armed so a
  // weak phone steps down the ladder rather than dropping frames.
  quality: { tier: "ultra", autoDowngrade: true },
  // The materialize-at-rest beat (#108): one deliberate fade — the finished cushion
  // blends in from nothing at its rest pose over ~2.4s (the cushion never moves).
  // A Stage Lab tune.
  reveal: {
    durationSec: 2.4,
    // A gentle ~9° yaw hint over ~1.1s right after the reveal, then quiet (#215).
    nudge: DEFAULT_NUDGE_CONFIG,
  },
};
